import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { listSavedShifts, type SavedShiftItem } from "@/lib/saved-shifts.functions";
import { SaveShiftButton } from "@/components/SaveShiftButton";
import { ReserveHeader } from "@/components/ReserveHeader";

export const Route = createFileRoute("/_authenticated/bartender/saved")({
  head: () => ({ meta: [
    { title: "Saved shifts — The Hospitality Reserve" },
    { name: "description", content: "Shifts you've bookmarked for later." },
  ] }),
  component: SavedPage,
});

function SavedPage() {
  const fetchList = useServerFn(listSavedShifts);
  const [items, setItems] = useState<SavedShiftItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchList().then((r) => { setItems(r.items ?? []); setLoading(false); });
  }, [fetchList]);

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <div className="max-w-3xl mx-auto p-6">
        <Link to="/bartender" className="text-xs tracking-wide-luxury text-muted-foreground hover:text-foreground transition-colors">
          ← Back to dashboard
        </Link>
        <h1 className="font-display text-2xl mt-2">Saved shifts</h1>
        <p className="text-muted-foreground text-sm mt-1">Your private shortlist. Save shifts from the marketplace to keep them handy.</p>
      {loading ? (
        <p className="mt-6 text-sm text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? (
        <p className="mt-8 text-sm text-muted-foreground">
          You haven't saved anything yet. <Link to="/bartender" className="underline text-accent">Browse shifts</Link>.
        </p>
      ) : (
        <ul className="mt-6 divide-y divide-border border border-border">
          {items.map((it) => {
            const s = it.shifts;
            if (!s) return null;
            const stillOpen = s.status === "open" && new Date(s.start_time) > new Date();
            return (
              <li key={it.shift_id} className="p-4 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium">{s.role_type} {s.postcode ? <span className="text-muted-foreground font-normal">· {s.postcode}</span> : null}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(s.start_time).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} — £{Number(s.hourly_rate).toFixed(2)}/hr
                  </p>
                  {!stillOpen && <p className="text-[11px] text-amber-600 mt-1">No longer available</p>}
                </div>
                <div className="flex flex-col items-end gap-2">
                  {stillOpen && <Link to="/bartender" className="text-xs underline text-accent">View</Link>}
                  <SaveShiftButton shiftId={s.id} compact />
                </div>
              </li>
            );
          })}
        </ul>
      )}
      </div>
    </div>
  );
}
